'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { getChainAddress } from '@morpho-org/morpho-sdk/addresses';
import { isDefined } from '@morpho-org/morpho-ts';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { Status } from '@/contexts/Transaction/StateMachine/helpers';
import type { InKindRedemptionReceiptAmounts } from './inKindRedemption.helpers';
import { realisedInKindRedemption } from './inKindRedemption.helpers';
export type InKindRedemptionOutcome = /** Nothing has landed yet, so the preview is still the only honest thing to show. */
{
  kind: 'pending';
}
/** The receipt decodes as this redemption; these are the amounts it really produced. */ | {
  kind: 'redeemed';
  amounts: InKindRedemptionReceiptAmounts;
}
/** A receipt landed and it was not a redemption — nothing about it may be presented as one. */ | {
  kind: 'unrecognised';
};

/**
 * What the confirmed transaction turned out to be.
 *
 * The confirm dialog's success state is reached by replacement detection too, which returns the
 * receipt of whatever landed on this nonce: a wallet cancellation, or an unrelated transaction
 * the user replaced this one with. Those receipts confirm successfully and mark the flow
 * complete, so the only thing separating them from a redemption is what the logs say — a
 * redemption's three legs account for its whole withdrawal, and nothing else satisfies that
 * identity.
 *
 * The distinction that matters is between "no receipt yet" and "a receipt landed and it was not
 * this redemption". The first is the ordinary pre-confirmation state; the second is a claim the
 * surfaces must not make, and neither the amounts nor the "manage this position" callout may
 * survive it. We deliberately say no more than that: a cancellation and an unrelated
 * same-nonce replacement are indistinguishable from here.
 */
export function useInKindRedemptionOutcome(t0) {
  const $ = _c(10);
  const {
    vaultAddress,
    marketId,
    assetAddress,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    status,
    step,
    receipt,
    account,
    chainId
  } = useTransactionContextV2();
  const hasLanded = enabled && status === Status.complete && step === "final";
  if (!hasLanded || !isDefined(receipt) || !isDefined(account?.address) || !isDefined(assetAddress)) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        kind: "pending"
      };
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== account.address || $[2] !== assetAddress || $[3] !== chainId || $[4] !== marketId || $[5] !== receipt.logs || $[6] !== vaultAddress) {
    t2 = realisedInKindRedemption(receipt.logs, {
      vaultAddress,
      marketId,
      assetAddress,
      userAddress: account.address,
      morphoAddress: getChainAddress(chainId, "blue"),
      bundlerAddress: getChainAddress(chainId, "bundles.vaultExitBundlesV1")
    });
    $[1] = account.address;
    $[2] = assetAddress;
    $[3] = chainId;
    $[4] = marketId;
    $[5] = receipt.logs;
    $[6] = vaultAddress;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const amounts = t2;
  let t3;
  if ($[8] !== amounts) {
    t3 = amounts ? {
      kind: "redeemed",
      amounts
    } : {
      kind: "unrecognised"
    };
    $[8] = amounts;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}